import Promise from "bluebird";
import ko from "knockout";
import apiCore from "services/apiCore";
import communicationService from "services/communicationService";
import LOG from "services/loggingService";
import ScreenMonitor from "models/ScreenMonitor";
import { withRequestRetry } from "util/withRequestRetry";
import errorCallbackFactory from "util/errorCallbackFactory";
import screenMonitoringUtils from "util/screenMonitoringUtils";
import participantStatus from "enums/participantStatus";
import featureToggleService, {
    FeatureToggle,
} from "services/featureToggleService";

interface GetScreenMonitorOptions {
    shouldReturnRawData?: boolean;
}

interface ApiCoreV2 {
    get: (path: string, parameters?: any) => Promise<any>;
    post: (path: string, data?: any, parameters?: any) => Promise<any>;
    del: (path: string, parameters?: any) => Promise<any>;
}

const apiCoreV2 = (apiCore as any).v2 as ApiCoreV2;

const MAX_RETRIES = 10;
// Timeout for waiting for user to accept call in volt UI and participant to be added
// Recommended: 30-45 seconds to account for:
// - Time for volt UI to load (1-2 seconds)
// - User to see and accept the call (5-20 seconds typical)
// - Backend to process and add participant (1-2 seconds)
const INITIAL_POLL_DELAY_MS = 2000; // Wait 2 seconds before first poll to give volt UI time to open
const MAX_POLLING_ATTEMPTS = 15;
const BACKOFF_MULTIPLIER = 1.2;
const BASE_BACKOFF_DELAY_MS = 1000;
const MAX_BACKOFF_DELAY_MS = 3000;

/**
 * Observable array of all active screen monitors.
 *
 * This is defined as a separate constant (rather than directly in the service object)
 * to avoid circular reference errors during initialization. When a computed property
 * inside the service object needs to reference `screenMonitoringService.activeScreenMonitors`,
 * the service object doesn't exist yet during its own initialization, causing a
 * "Cannot access before initialization" error.
 *
 * By creating the observable array first, we can:
 * 1. Reference it directly in computed functions without needing the service object
 * 2. Still assign it to the service object so it's accessible via `screenMonitoringService.activeScreenMonitors`
 * 3. Follow the same pattern used in other services (messageService, callService)
 *
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Errors/Cant_access_lexical_declaration_before_init|MDN: Temporal Dead Zone}
 */
const activeScreenMonitors = ko.observableArray<any>([]);

/**
 * Screen monitoring service. Handles screen monitoring for all conversation types.
 * @exports services/screenMonitoringService
 */
const screenMonitoringService: any = {
    uriBuilder: communicationService.createUriBuilder("/conversations"),

    /**
     * Observable array of all active screen monitors
     */
    activeScreenMonitors: activeScreenMonitors,

    /**
     * Observable array of all active user screen monitors. This is a subset of activeScreenMonitors.
     */
    activeUserScreenMonitors: ko.computed(function (): any[] {
        return activeScreenMonitors().filter(function (screenMonitor: any) {
            return (
                screenMonitor.isUserScreenMonitor &&
                screenMonitor.isUserScreenMonitor()
            );
        });
    }),

    // ===============================================================================
    // Methods
    // ===============================================================================

    getScreenMonitor: function (
        conversationId: string,
        options?: GetScreenMonitorOptions
    ) {
        const opts = Object.assign(
            {
                shouldReturnRawData: false,
            },
            options
        );

        return communicationService
            .getCommunication(this.uriBuilder, conversationId)
            .then(function (conversationData: any) {
                if (opts.shouldReturnRawData) {
                    return conversationData;
                }

                return new (ScreenMonitor as any)(conversationData);
            });
    },

    /**
     * Get the screen monitors for a current user
     * @return {Promise}
     */
    getActiveScreenMonitors: function () {
        return apiCoreV2.get(
            screenMonitoringService.uriBuilder.communicationsByType(
                "screen_monitoring"
            )
        );
    },

    /**
     * Start screen monitoring for any conversation type
     * @param {String} conversationId the id of the conversation to monitor
     * @param {String} participantId the id of the participant to monitor
     * @return {promise} a promise that resolves with the screen monitoring response
     */
    startScreenMonitoring: function (
        conversationId: string,
        participantId: string
    ) {
        const self = this;

        return apiCoreV2
            .post(
                screenMonitoringService.uriBuilder.conversationLevelScreenMonitor(
                    conversationId,
                    participantId
                )
            )
            .then(function () {
                // Open new tab with /volt/#/staging?monitoringClient=true URL using current host
                const monitorUrl = new URL(
                    "/volt/#/staging?monitoringClient=true",
                    window.location.origin
                );
                window.open(monitorUrl.toString(), "_blank");

                return waitForNewScreenMonitorToBeCreated(conversationId);
            })
            .then(function (screenMonitor: any) {
                return communicationService.validateActionSucceeded(
                    screenMonitor.id,
                    screenMonitor.participantForCurrentUser().id,
                    ScreenMonitor,
                    "status",
                    participantStatus.CONNECTED,
                    self.activeScreenMonitors,
                    self.uriBuilder
                );
            });
    },

    /**
     * Stop screen monitoring for any conversation type
     * @param {String} conversationId the id of the conversation being monitored
     * @param {String} participantId the id of the participant being monitored
     * @param {String} screenMonitoringId the id of the screen monitoring session
     * @return {promise} a promise that resolves with the screen monitoring response
     */
    stopConversationLevelScreenMonitoring: function (
        conversationId: string,
        participantId: string,
        screenMonitoringId: string
    ) {
        const self = this;

        if (!conversationId) {
            return Promise.reject(new Error("Conversation ID is required"));
        }

        if (!participantId) {
            return Promise.reject(new Error("Participant ID is required"));
        }

        if (!screenMonitoringId) {
            return Promise.reject(
                new Error("Screen monitoring ID is required")
            );
        }

        return apiCoreV2
            .del(
                this.uriBuilder.stopConversationLevelScreenMonitor(
                    conversationId,
                    participantId,
                    screenMonitoringId
                )
            )
            .then(function () {
                return communicationService.validateActionSucceeded(
                    conversationId,
                    participantId,
                    ScreenMonitor,
                    "status",
                    participantStatus.DISCONNECTED,
                    self.activeScreenMonitors,
                    self.uriBuilder
                );
            });
    },

    /**
     * Stop agent-level screen monitoring
     * @param {String} userId the id of the user whose screen monitoring session to stop
     * @param {String} screenMonitoringId the id of the screen monitoring session
     * @return {promise} a promise that resolves with the screen monitoring response
     */
    stopAgentLevelScreenMonitoring: function (
        userId: string,
        screenMonitoringId: string
    ) {
        if (!screenMonitoringId) {
            return Promise.reject(
                new Error("Screen monitoring ID is required")
            );
        }

        if (!userId) {
            return Promise.reject(new Error("User ID is required"));
        }

        return apiCoreV2
            .del(
                this.uriBuilder.stopAgentLevelScreenMonitor(
                    userId,
                    screenMonitoringId
                )
            )
            .then(function (response: any) {
                LOG.info(
                    "Agent-level screen monitoring stopped for user: " + userId
                );
                return response;
            });
    },
};

// Conditionally subscribe to topic when feature toggle is enabled
// Use observable pattern to react to feature toggle changes
const screenMonitoringToggle = featureToggleService.getFeatureToggle(
    FeatureToggle.WEM_437_SCREEN_MONITORING
);

// Subscribe to feature toggle changes and subscribe to topic when enabled
let topicSubscribed = false;
screenMonitoringToggle.subscribe(function (isEnabled: boolean) {
    if (isEnabled && !topicSubscribed) {
        try {
            communicationService.subscribeToTopic(
                "conversations.screenMonitors",
                screenMonitoringService.activeScreenMonitors,
                screenMonitoringService.getActiveScreenMonitors,
                ScreenMonitor
            );
            topicSubscribed = true;
            // Note: subscribeToTopic uses ko.computed internally, so it will handle
            // activation when user is logged in and subscriptions are available
        } catch (error) {
            // Subscription might already exist, ignore error
            LOG.error("Error subscribing to screen monitoring topic", error);
        }
    }
});

/**
 * Pushes screen monitor data into active screen monitors, replacing any missing data,
 * but never overwriting existing data from push events. This
 * behavior ensures eventual consistency, provided that the
 * screenMonitorData is fetched after activeScreenMonitors becomes live.
 *
 * @param {object} screenMonitorData raw screen monitor data to push to active screen monitors
 */
const fillToActiveScreenMonitors = function (screenMonitorData: any) {
    let screenMonitor = screenMonitoringService
        .activeScreenMonitors()
        .find((monitor: any) => monitor.id === screenMonitorData.id);
    if (screenMonitor) {
        LOG.debug(
            "Filling data into active screen monitors",
            screenMonitorData
        );
        (ScreenMonitor as any).fill(screenMonitor, screenMonitorData);
    } else {
        LOG.debug(
            "Adding screen monitor to activeScreenMonitors",
            screenMonitorData
        );
        screenMonitor = addToActiveScreenMonitors(screenMonitorData);
    }
    return screenMonitor;
};

/**
 * Adds a new screen monitor object to the list of active screen monitors based on
 * the provided screen monitor data, and sets up tracking so the screen monitor
 * will be removed when the user disconnects.
 * @param {object} screenMonitorData raw screen monitor data to add to active screen monitors
 */
const addToActiveScreenMonitors = function (screenMonitorData: any) {
    const screenMonitor = new (ScreenMonitor as any)(screenMonitorData);
    screenMonitoringService.activeScreenMonitors.push(screenMonitor);
    return screenMonitor;
};

/**
 * Finds a screen monitor by conversation ID in the active screen monitors array
 * @param {string} conversationId - The conversation ID to search for
 * @returns {any} The screen monitor if found, undefined otherwise
 */
const findScreenMonitorById = function (conversationId: string): any {
    return activeScreenMonitors().find(
        (screenMonitor: any) => screenMonitor.id === conversationId
    );
};

/**
 * Checks if a screen monitor meets the waiting condition (targetUser.id with connected state)
 * @param {any} screenMonitor - The screen monitor to check
 * @returns {boolean} True if condition is met, false otherwise
 */
const checkWaitingCondition = function (screenMonitor: any): boolean {
    if (!screenMonitor) {
        return false;
    }

    const participant = screenMonitor.participantForCurrentUser();
    if (!participant) {
        return false;
    }

    // Wait for targetUser.id with connected state
    const screenMonitorings = participant.screenMonitorings;
    const targetUserId =
        screenMonitoringUtils.getConnectedTargetUserId(screenMonitorings);
    return targetUserId !== null;
};

/**
 * Waits for a new screen monitor to be created and for the participant to be added
 * to the conversation after the user accepts the call in the volt UI.
 *
 * Flow: POST (201) → Volt UI opens → User accepts call → Backend adds participant → Poll until CONNECTED
 *
 * @param {string} conversationId - The ID of the conversation being monitored
 *
 * @returns {Promise<any>} Resolves with ScreenMonitor when participant has connected screenMonitoring state
 *   (targetUser.id with connected state). Rejects on timeout (after 15 polling attempts) or error.
 *
 * @description
 * ## Timing Details
 *
 * - **Initial Delay**: {@link INITIAL_POLL_DELAY_MS} (2s) - Gives volt UI time to open before first poll
 * - **Polling Intervals**: Exponential backoff `min(BASE_BACKOFF_DELAY_MS * BACKOFF_MULTIPLIER^tries, MAX_BACKOFF_DELAY_MS)` - starts at ~1s, increases to max 3s
 * - **Max Polling Attempts**: {@link MAX_POLLING_ATTEMPTS} attempts - After initial delay, polls up to MAX_POLLING_ATTEMPTS times with exponential backoff
 * - **withRequestRetry**: {@link MAX_RETRIES} (10 retries) at 1s intervals for active monitors polling when screenMonitor exists
 *
 * Polls every 1-3s until participant with targetUser.id (connected state) is found, or timeout after MAX_POLLING_ATTEMPTS attempts.
 */
const waitForNewScreenMonitorToBeCreated = function (conversationId: string) {
    let resolvePromise!: (value: any) => void;
    let rejectPromise!: (error: any) => void;
    const promise = new Promise((resolve, reject) => {
        resolvePromise = resolve;
        rejectPromise = reject;
    });

    // Check if screenMonitor already exists
    const screenMonitor = findScreenMonitorById(conversationId);

    if (screenMonitor && checkWaitingCondition(screenMonitor)) {
        // Already have what we need
        resolvePromise(screenMonitor);
        return promise;
    }

    // If screenMonitor exists but condition not met, poll for updates
    if (screenMonitor) {
        withRequestRetry(
            () => screenMonitoringService.getActiveScreenMonitors(),
            (conversationsData: any): boolean => {
                const entity = conversationsData.entities?.find(
                    (entity: any) => entity.id === conversationId
                );
                if (!entity?.participants?.length) {
                    return true;
                }

                // Find current user's participant
                const currentUserParticipant =
                    screenMonitor.participantForCurrentUser();
                if (!currentUserParticipant) {
                    return true;
                }

                const foundParticipant = entity.participants.find(
                    (participant: any) =>
                        participant.purpose === "user" &&
                        currentUserParticipant.userId() ===
                            participant.userId &&
                        !participant.endTime
                );

                if (!foundParticipant) {
                    return true;
                }

                // Update screenMonitor with latest data from API response
                const updatedScreenMonitor = fillToActiveScreenMonitors(entity);
                if (
                    updatedScreenMonitor &&
                    checkWaitingCondition(updatedScreenMonitor)
                ) {
                    resolvePromise(updatedScreenMonitor);
                    return false;
                }

                return true;
            },
            (error: any): boolean => {
                rejectPromise(error);
                errorCallbackFactory.createCallback({
                    namespace: "screenMonitorRequest.error",
                })(error);
                return false;
            },
            { maxRetries: MAX_RETRIES, startIntervalMs: 1000 }
        )();

        return promise;
    }

    // ScreenMonitor doesn't exist yet - poll until it's created
    const pollingObj = {
        timeout: null as number | null,
        xhrRequest: null as any,
    };

    const subscription = screenMonitoringService.activeScreenMonitors.subscribe(
        function () {
            const screenMonitor = findScreenMonitorById(conversationId);

            if (screenMonitor && checkWaitingCondition(screenMonitor)) {
                resolvePromise(screenMonitor);
                cleanup();
            }
        }
    );

    const cleanup = function () {
        if (pollingObj.timeout) {
            clearTimeout(pollingObj.timeout);
            pollingObj.timeout = null;
        }
        if (pollingObj.xhrRequest) {
            pollingObj.xhrRequest.cancel();
            pollingObj.xhrRequest = null;
        }
        if (subscription) {
            subscription.dispose();
        }
    };

    let tries = 0;

    const pollForScreenMonitor = function () {
        pollingObj.xhrRequest = screenMonitoringService.getScreenMonitor(
            conversationId,
            { shouldReturnRawData: true }
        );

        pollingObj.xhrRequest
            .then(function (conversationData: any) {
                if (
                    !conversationData ||
                    !("participants" in conversationData)
                ) {
                    return;
                }

                let newScreenMonitor = new (ScreenMonitor as any)(
                    conversationData
                );
                if (!newScreenMonitor.isDisconnected()) {
                    newScreenMonitor =
                        fillToActiveScreenMonitors(conversationData);
                }

                // Check if condition is met
                if (checkWaitingCondition(newScreenMonitor)) {
                    cleanup();
                    resolvePromise(newScreenMonitor);
                    return;
                }

                // If disconnected, stop polling
                if (newScreenMonitor.isDisconnected()) {
                    cleanup();
                    resolvePromise(newScreenMonitor);
                    return;
                }
            })
            .catch(Promise.CancellationError, () => {})
            .finally(function () {
                const screenMonitor = findScreenMonitorById(conversationId);

                // If found and condition met, we're done
                if (screenMonitor && checkWaitingCondition(screenMonitor)) {
                    cleanup();
                    resolvePromise(screenMonitor);
                    return;
                }

                // Check timeout - MAX_POLLING_ATTEMPTS polling attempts after initial delay
                // With exponential backoff (1-3s per attempt), this provides sufficient time
                // for user to accept call in volt UI and participant to be added
                if (tries >= MAX_POLLING_ATTEMPTS) {
                    cleanup();
                    rejectPromise(
                        new Error("Timeout waiting for screen monitor creation")
                    );
                    return;
                }

                // Continue polling with exponential backoff
                const delayMs = Math.min(
                    BASE_BACKOFF_DELAY_MS * Math.pow(BACKOFF_MULTIPLIER, tries),
                    MAX_BACKOFF_DELAY_MS
                );
                pollingObj.timeout = window.setTimeout(
                    pollForScreenMonitor,
                    delayMs
                );
                tries++;
            });
    };

    // Start polling after initial delay
    pollingObj.timeout = window.setTimeout(
        pollForScreenMonitor,
        INITIAL_POLL_DELAY_MS
    );

    return promise;
};
export default screenMonitoringService;
